import { createSelector } from "@reduxjs/toolkit";
import type { ICeilingGridItem } from "@multiconsult-group/multiplacer-core";
import type { RoomSectionRenderItem } from "@multiconsult-group/multiplacer-ui";
import { selectHttpMcbimSpacesResult } from "../McBim/httpMcBimSelectors";

export const CEILING_GRID_3D_ID_PREFIX = "ceiling-grid:";
// Display width/depth for grid lines, not a manufacturer's profile specification.
const GRID_PROFILE_SIZE_M = 0.015;
type GridSpace = {
  _id?: unknown;
  globalId?: string;
  name: string;
  ceilingGrids?: ICeilingGridItem[];
};

export const buildCeilingGrid3dItems = (spaces: GridSpace[]): RoomSectionRenderItem[] =>
  spaces.flatMap((space) =>
    (space.ceilingGrids ?? []).flatMap((grid, gridIndex) => {
      const obbs = (grid.lines ?? []).flatMap((line) => {
        const points = line.map((point) => [
          point[0],
          point[1],
          point[2] ??
            grid.worldHeightM ??
            (grid.worldHeightMm != null ? grid.worldHeightMm / 1000 : NaN),
        ]);
        if (points.length !== 2 || !points.every((point) => point.every(Number.isFinite)))
          return [];
        const [start, end] = points;
        const dx = end[0] - start[0],
          dy = end[1] - start[1],
          dz = end[2] - start[2];
        const length = Math.hypot(dx, dy, dz);
        const horizontalLength = Math.hypot(dx, dy);
        if (length < 1e-6) return [];
        const axisA = { x: dx / length, y: dy / length, z: dz / length };
        const axisB =
          horizontalLength > 1e-6
            ? { x: -dy / horizontalLength, y: dx / horizontalLength, z: 0 }
            : { x: 1, y: 0, z: 0 };
        return [
          {
            centerM: {
              x: (start[0] + end[0]) / 2,
              y: (start[1] + end[1]) / 2,
              z: (start[2] + end[2]) / 2,
            },
            axisA,
            axisB,
            axisC: {
              x: -axisA.z * axisB.y,
              y: axisA.z * axisB.x,
              z: axisA.x * axisB.y - axisA.y * axisB.x,
            },
            extentAM: length / 2,
            extentBM: GRID_PROFILE_SIZE_M / 2,
            extentCM: GRID_PROFILE_SIZE_M / 2,
          },
        ];
      });
      if (!obbs.length) return [];
      return [
        {
          item: {
            _id: `${CEILING_GRID_3D_ID_PREFIX}${space._id ?? space.globalId ?? space.name}:${gridIndex}`,
            geometry: { Obb3DWorldMmList: obbs },
          },
          color: "#aeb5bb",
          opacity: 1,
        },
      ];
    })
  );

export const selectCeilingGrid3dItems = createSelector(
  [selectHttpMcbimSpacesResult],
  buildCeilingGrid3dItems
);
